import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
  type MouseEventHandler,
  type ReactNode,
  type Ref,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { twMerge } from "tailwind-merge";
import { useMenuDrawerContext } from "../menu/MenuDrawerContextProvider.shared";
import { NavigationBarSubHeader } from "./NavigationBar";
import { zLayer } from "@sellar-io/components";

type NavigationBarSearchOverlayControls = {
  close: () => void;
  open: boolean;
};

type SearchInputProps = {
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  portalRoot?: HTMLElement | null;
  close: () => void;
  suggestionsEnabled?: boolean;
};

type NavigationBarSearchOverlayProps = {
  children: (controls: NavigationBarSearchOverlayControls) => ReactNode;
  hasDashboardSidebar: boolean;
  navigationBar: (controls: NavigationBarSearchOverlayControls) => ReactNode;
  searchInput: (props: SearchInputProps) => ReactNode;
};

export const NavigationBarSearchOverlay = ({
  children,
  hasDashboardSidebar,
  navigationBar,
  searchInput,
}: NavigationBarSearchOverlayProps) => {
  const { isMobileDisplayType, isRailDisplayType } = useMenuDrawerContext();
  const [open, setOpen] = useState(false);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const dialogInputRef = useRef<HTMLInputElement>(null);
  const close = () => setOpen(false);
  const openSearch: MouseEventHandler<HTMLDivElement> = (event) => {
    // Portalled popup clicks still bubble through React's trigger wrapper.
    if (
      event.target instanceof Node &&
      !event.currentTarget.contains(event.target)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    flushSync(() => setOpen(true));
    dialogInputRef.current?.focus();
  };

  const contentInsetClassName =
    !hasDashboardSidebar || isMobileDisplayType
      ? "left-0"
      : isRailDisplayType
        ? "left-[60px]"
        : "left-[250px]";
  const backgroundClassName =
    import.meta.env.VITE_ENV === "production" ? "bg-white" : "bg-[#ffc6a1]";

  return (
    <>
      {navigationBar({ open: false, close })}
      <NavigationBarSubHeader>
        <div
          className="z-10 m-auto w-full max-w-[1200px] overflow-hidden p-4"
          onClick={openSearch}
        >
          {searchInput({ close, suggestionsEnabled: !open })}
        </div>
      </NavigationBarSubHeader>

      <Dialog
        open={open}
        onClose={close}
        className={`relative ${zLayer.dialog}`}
      >
        <DialogBackdrop
          transition
          className={twMerge(
            "fixed right-0 bottom-0 bg-black/50 transition duration-200 ease-out data-closed:opacity-0",
            contentInsetClassName,
            isMobileDisplayType
              ? "top-[calc(var(--refresh-pwa-banner-height)+var(--hidden-platform-banner-height))]"
              : "top-0",
          )}
        />
        <DialogPanel
          ref={setPortalRoot}
          transition
          className={twMerge(
            `fixed right-0 ${zLayer.dialog} flex flex-col transition duration-200 ease-out data-closed:-translate-y-2 data-closed:opacity-0`,
            contentInsetClassName,
            isMobileDisplayType
              ? "top-[calc(var(--refresh-pwa-banner-height)+var(--hidden-platform-banner-height))] h-[calc(100dvh-var(--refresh-pwa-banner-height)-var(--hidden-platform-banner-height))] max-h-none"
              : "top-0 max-h-dvh",
          )}
        >
          {navigationBar({ open: true, close })}
          <div
            className={twMerge(
              "flex min-h-0 flex-col border-b border-solid",
              backgroundClassName,
              isMobileDisplayType && "flex-1",
            )}
          >
            <div className="m-auto w-full max-w-[1200px] shrink-0 overflow-hidden p-4">
              {searchInput({
                autoFocus: true,
                inputRef: dialogInputRef,
                portalRoot,
                close,
              })}
            </div>
            <div
              className={twMerge(
                "m-auto flex min-h-0 w-full max-w-[1200px] flex-col overflow-hidden px-4 pb-4",
                isMobileDisplayType && "flex-1",
              )}
            >
              {children({ open: true, close })}
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
};
